<template>
  <section
    class="page__section"
    :style="componentStyle"
  >
    <div class="container">
      <div
        class="editor editor__content editor__page"
        v-if="translate(text_editor)"
        v-html="translate(text_editor)"
      />

      <div class="margin__t--18">
        <app-link
          :link="button"
          :style="buttonStyle"
          class="btn btn__size--medium btn__color--prim d-inline-block"
        />
      </div>

      <form
        class="filters margin__t--36"
        v-if="show_filters"
        @submit.prevent.stop
      >
        <fieldset class="filters__fieldset">
          <label
            class="sr-only"
            :for="`input_search_${section.id}`"
          >
            {{ t('literal.search_for_a_project') }}
          </label>

          <search-field
            :id="`input_search_${section.id}`"
            :placeholder="translate(filters.search)"
            v-model="query.search"
          />
        </fieldset>

        <fieldset class="filters__fieldset">
          <label
            class="sr-only"
            :for="`input_sort_${section.id}`"
          >
            {{ t('literal.sorted_by') }}
          </label>

          <input-select
            :id="`input_sort_${section.id}`"
            class="filters__select"
            :options="projectsSortByOptions"
            v-model="query.sort_by"
          />
        </fieldset>

        <fieldset class="filters__fieldset">
          <label
            class="sr-only"
            :for="`input_date_${section.id}`"
          >
            {{ t('literal.filter_by_event_dates') }}
          </label>

          <v-date-picker
            class="datepicker"
            color="red"
            v-model.range="eventDates"
          >
            <template #default="{ togglePopover }">
              <button
                type="button"
                class="event-dates"
                @click="togglePopover"
              >
                <span class="event-dates__text">
                  {{ dateFormated || translate(filters.date) }}
                </span>

                <fa-icon
                  class="event-dates__icon"
                  :icon="['fas', 'chevron-down'] "
                />
              </button>
            </template>
          </v-date-picker>
        </fieldset>

        <fieldset class="filters__fieldset">
          <label
            class="sr-only"
            :for="`input_benefitings_${section.id}`"
          >
            {{ t('literal.filter_by_beneficiary') }}
          </label>

          <input-select-benefitings
            :id="`input_benefitings_${section.id}`"
            class="select filters__select"
            :placeholder="translate(filters.beneficiaries)"
            v-model="query.benefiting_id"
          />
        </fieldset>
      </form>

      <section class="projects">
        <div
          class="spinner__container"
          v-if="loader"
        >
          <app-spinner :size="5" />
        </div>

        <div
          class="row"
          v-else-if="projects.length"
        >
          <div
            class="col-12 col-sm-6 col-md-4 col-xl-3"
            v-for="project in projects"
            :key="project.id"
          >
            <project-card :project="project" />
          </div>
        </div>

        <div
          class="projects__none"
          :style="{ 'color': componentStyle.color }"
          v-else
        >
          <fa-icon
            class="margin__r--6"
            :icon="['fal', 'search']"
          />

          {{ t('projects.no_results') }}
        </div>

        <app-pagination
          v-if="show_pagination"
          class="margin__t--24"
          :paginator="pagination"
          :colors="{ text_color: componentStyle.color, button_text_color: buttonStyle.color, button_background_color: buttonStyle.backgroundColor }"
          @change="page => query.page = page"
        />
      </section>
    </div>
  </section>
</template>

<script setup>
import { defineAsyncComponent, ref, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useProjectStore } from '@/stores/ProjectStore'
import { useStyle } from '@/composables/app/useStyle'
import { useQuery } from '@/composables/app/useQuery'
import i18n, { useI18n } from '@/vendors/i18n'
import { diffObjects } from '@/utils/utils'
import { get as _get } from 'lodash'
import ProjectQuery from '@/classes/queries/ProjectQuery'
import { isSameDay, startOfDay, format } from 'date-fns'

import 'v-calendar/dist/style.css'

import { DatePicker as VDatePicker } from 'v-calendar'
const ProjectCard = defineAsyncComponent(() => import('&/organisms/ProjectCard'))
const AppLink = defineAsyncComponent(() => import('&/atoms/AppLink'))
const AppSpinner = defineAsyncComponent(() => import('&/atoms/AppSpinner'))
const InputSelectBenefitings = defineAsyncComponent(() => import('&/atoms/InputSelectBenefitings'))
const InputSelect = defineAsyncComponent(() => import('&/atoms/InputSelect'))
const AppPagination = defineAsyncComponent(() => import('&/molecules/AppPagination'))
const SearchField = defineAsyncComponent(() => import('&/molecules/SearchField'))

const { section, index } = defineProps({
  section: { type: Object, required: true },
  index: { type: Number, default: 0 }
})

const { t, translate } = useI18n()
const { componentStyle, buttonStyle } = useStyle(section.style)
const { encodeQueryParams, decodeQueryParams } = useQuery()
const { getProjects } = useProjectStore()
const route = useRoute()
const { replace } = useRouter()

const { text_editor, button, filters, show_filters, show_pagination } = section.attributes
const { sort = 'activity_last', campaign_id, benefiting_id, per_page = 12 } = section.attributes.query

const decodedQueryParams = computed(() => _get(decodeQueryParams({ query: route.query }), index, {}))

const default_query = new ProjectQuery({ sort_by: sort, campaign_id, benefiting_id, per_page })

const query = ref(
  new ProjectQuery({
    search: decodedQueryParams.value.search,
    sort_by: decodedQueryParams.value.sort_by || default_query.sort_by,
    campaign_id: decodedQueryParams.value.campaign_id || default_query.campaign_id,
    benefiting_id: decodedQueryParams.value.benefiting_id || default_query.benefiting_id,
    per_page: default_query.per_page,
    page: decodedQueryParams.value.page,
    event_start_date: decodedQueryParams.value.event_start_date,
    event_end_date: decodedQueryParams.value.event_end_date
  })
)

const queryToUrl = computed(() => diffObjects(default_query.to_url, query.value.to_url))

const projectsSortByOptions = computed(() => [
  { label: i18n.getLocalesTranslations('literal.last_activity'), value: 'activity_last' },
  { label: i18n.getLocalesTranslations('literal.latest_created_first'), value: 'created_at_first' },
  { label: i18n.getLocalesTranslations('literal.latest_created_last'), value: 'created_at_last' },
  { label: { ...i18n.getLocalesTranslations('literal.event_end_soon_first'), 'nl-NL': 'Bijna afgelopen eerst' }, value: 'event_end_first' }
])

const eventDates = computed({
  get: () => {
    const start = query.value.event_start_date ? new Date(query.value.event_start_date) : null
    const end = query.value.event_end_date ? new Date(query.value.event_end_date) : null

    return ({ start, end: end ?? start })
  },
  set: ({ start, end }) => {
    if (isSameDay(start, end)) {
      query.value.event_start_date = startOfDay(start)
      query.value.event_end_date = null
    } else {
      query.value.event_start_date = startOfDay(start)
      query.value.event_end_date = startOfDay(end)
    }
  }
})

const dateFormated = computed(() => {
  if (!eventDates.value.start) return ''
  if (isSameDay(eventDates.value.start, eventDates.value.end)) return format(eventDates.value.start, 'dd/MM/yy')

  return `${format(eventDates.value.start, 'dd/MM')} - ${format(eventDates.value.end, 'dd/MM')}`
})

const { projects, pagination, loader } = getProjects({ query })

watch(() => ({ ...query.value }), (to, from) => {
  if (to.page !== 1 && to.page === parseInt(from.page)) return query.value.page = 1

  const currentQuery = { ...route.query }
  const encodedQueryParams = encodeQueryParams(queryToUrl.value)

  encodedQueryParams ? currentQuery[index] = encodedQueryParams : delete currentQuery[index]

  replace({ query: currentQuery })
})
</script>

<style scoped lang="scss">
$color--blue: #050C2B;
$color--lightblue: #005aeb;

.filters {
  background: $color--lightblue;
  border-radius: 12px;
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;

  @include mq(sm) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @include mq(md) {
    padding: 0 24px;
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  @include mq(xl) {
    padding: 0 36px;
    grid-template-columns: 320px 1fr 1fr 320px;
  }

  &__fieldset {
    position: relative;
    padding: 30px 12px;
    min-width: 0;
    box-sizing: border-box;

    &:not(:last-child) {
      border-bottom: 1px solid rgba(#FFFFFF, 0.45);
    }

    @include mq(sm) {
      padding: 30px 24px;

      &:nth-child(odd) {
        border-right: 1px solid rgba(#FFFFFF, 0.45);
      }
    }

    @include mq(md) {
      padding: 30px 12px;
      border: 0;

      &:not(:last-child),
      &:nth-child(odd) {
        border: 0;
      }
    }

    @include mq(xl) {
      padding: 48px 30px;
    }
  }

  &__select {
    &:deep() {
      .v-select {
        border: 2px solid $color--blue;
      }

      .vs {
        &__dropdown-menu {
          border: 2px solid $color--blue;
          box-sizing: border-box;
          top: 100%;
          width: calc(100% + 4px);
          left: -2px;
        }

        &__dropdown-option {
          font-size: rem(16px);
          padding: 12px;
          border-top: 1px solid $color--blue;
        }

        &--single {
          .vs__selected-options {
            min-width: 0;
          }

          .vs__dropdown-toggle {
            min-width: 0;
            overflow: hidden;
          }

          .vs__selected {
            font-size: rem(18px);
            line-height: 1;
            font-weight: bold;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            min-width: 0;
            flex: 1 1 0;
            display: block;
            padding: 6px 0 6px 6px;
            margin: 0;
            position: relative;
          }

          .vs__search {
            max-width: 0;
            padding: 0;
            height: 30px;
          }

          .vs__dropdown-option--selected {
            background-color: $color--lightblue;
          }
        }

        &--multiple {
          .vs__selected {
            background: $color--blue;
            color: $white;
          }

          .vs__search {
            font-size: rem(18px);
            line-height: 1;
            font-weight: bold;

            &::placeholder {
              color: $color--blue;
              font-size: rem(18px);
            }
          }

          svg[data-icon="square-check"],
          svg[data-icon="square"] {
            color: $color--blue;
          }
        }
      }
    }
  }

  :deep() {
    .search {
      padding: 0;
      border: 2px solid $color--blue;

      &__input {
        font-size: 18px;
        line-height: 1;
        font-weight: bold;
        color: $color--blue;
        border: 0;
        padding-right: 0;
        height: 42px;

        &:focus {
          border: 0;
        }

        &::placeholder {
          color: rgba($color--blue, 0.4)!important;
          font-size: rem(18px)!important;
        }
      }
    }
  }
}

.event-dates {
  border: 2px solid $color--blue;
  color: $color--blue;
  background: $white;
  font-weight: bold;
  font-size: rem(18px);
  line-height: 1;
  width: 100%;
  padding: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 4px;
  gap: 12px;

  &__text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }

  &__icon {
    padding: 0 3px;
    font-size: rem(9px);
  }
}

:deep() {
  .vc-pane-container {
    color: $color--blue;
  }

  .vc-highlight-bg-solid {
    background: $color--blue;
  }

  .vc-highlight-bg-light {
    background: rgba($color--blue, 0.7);
  }

  .vc-highlight-content-light {
    color: $white;
  }

  .vc-highlight-bg-outline,
  .vc-highlight-bg-none {
    border-color: $color--blue;
  }

  .vc-highlight-content-outline,
  .vc-highlight-content-none {
    color: $color--blue;
  }

  .vc-nav-item.is-active {
    background: $color--blue;
  }
}

.projects {
  margin-top: 80px;

  &__none {
    margin-top: 12px;
    color: $lg;
    text-align: center;
    font-size: rem(18px);
  }
}

.spinner__container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px 0;
}
</style>